import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import {
  UIChip,
  UIPaginationBar,
  UITable,
  UIUserInfo,
  getUserInitials,
} from "../../../../../ui";
import TruncatedWithTooltip from "../../../../../TruncatedWithTooltip";
import { formatPluralLabel } from "../../../../../../utils/Helpers";
import {
  formatPayrollAmount,
  formatPayrollCurrency,
} from "../utils/formatPayrollValue";

const ROLE_COUNT_PATTERN = /^(\d+)\s+Roles?$/i;
const DEFAULT_PAGE_SIZE = 10;

const getRoleCount = (roleSummary) => {
  const match = ROLE_COUNT_PATTERN.exec(String(roleSummary || "").trim());
  return match ? Number(match[1]) : 1;
};

const textColumn = (column) => ({
  align: "left",
  headerAlign: "left",
  sortable: false,
  ...column,
});

const EventPayrollBulkTable = ({
  rows,
  labels,
  currency,
  showStcNumber,
  rangeTemplate,
  rowsPerPageLabel,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [rows]);

  const total = rows.length;
  const pages = Math.max(Math.ceil(total / pageSize), 1);
  const rangeFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeTo = Math.min(page * pageSize, total);
  const pagedRows = useMemo(
    () => rows.slice((page - 1) * pageSize, page * pageSize),
    [rows, page, pageSize],
  );

  const handlePageSizeChange = (nextSize) => {
    setPageSize(nextSize);
    setPage(1);
  };

  const columns = useMemo(() => {
    const baseColumns = [
      textColumn({
        field: "id",
        headerName: labels.id,
        autoFlex: false,
        flex: 0,
        width: 120,
        minWidth: 120,
        maxWidth: 120,
        renderCell: ({ row }) => (
          <span className="MuiDataGrid-cellContent">#{row.id}</span>
        ),
      }),
      textColumn({
        field: "staff",
        headerName: labels.staff,
        minWidth: 260,
        renderCell: ({ row }) => (
          <UIUserInfo
            className="event-payroll-generate__staff"
            name={
              <TruncatedWithTooltip title={row.displayName}>
                <span className="MuiDataGrid-cellContent">
                  {row.displayName}
                </span>
              </TruncatedWithTooltip>
            }
            image={row.image}
            initials={getUserInitials(row.displayName)}
          >
            <UIChip
              label={formatPluralLabel(getRoleCount(row.roleSummary), labels.roleCount, lang)}
              shape="pill"
              size="small"
              className="event-payroll-generate__role-badge"
            />
          </UIUserInfo>
        ),
      }),
      textColumn({
        field: "days",
        headerName: labels.days,
        renderCell: ({ row }) => (
          <span className="MuiDataGrid-cellContent">{row.days}</span>
        ),
      }),
      textColumn({
        field: "shiftPay",
        headerName: labels.shiftPay,
        renderCell: ({ row }) => (
          <span className="MuiDataGrid-cellContent">
            {formatPayrollAmount(row.shiftPay)}
          </span>
        ),
      }),
      textColumn({
        field: "autoDeduction",
        headerName: labels.autoDeduction.replace(" ", "\n"),
        renderCell: ({ row }) => (
          <span className="MuiDataGrid-cellContent event-payroll-generate__amount--negative">
            {formatPayrollAmount(row.autoDeduction, { signed: true })}
          </span>
        ),
      }),
      textColumn({
        field: "manualDeduction",
        headerName: labels.manualDeduction.replace(" ", "\n"),
        renderCell: ({ row }) => (
          <span className="MuiDataGrid-cellContent event-payroll-generate__amount--negative">
            {formatPayrollAmount(row.manualDeduction, { signed: true })}
          </span>
        ),
      }),
      textColumn({
        field: "rewards",
        headerName: labels.rewards,
        renderCell: ({ row }) => (
          <span className="MuiDataGrid-cellContent event-payroll-generate__amount--positive">
            {formatPayrollAmount(row.rewards, { signed: true })}
          </span>
        ),
      }),
      textColumn({
        field: "trainingDays",
        headerName: labels.trainingDays,
        renderCell: ({ row }) => (
          <span className="MuiDataGrid-cellContent">{row.trainingDays}</span>
        ),
      }),
      textColumn({
        field: "trainingPay",
        headerName: labels.trainingPay,
        renderCell: ({ row }) => (
          <span className="MuiDataGrid-cellContent">
            {formatPayrollAmount(row.trainingPay)}
          </span>
        ),
      }),
      textColumn({
        field: "totalAmount",
        headerName: labels.totalAmount,
        renderCell: ({ row }) => (
          <span
            className="MuiDataGrid-cellContent"
            style={{ color: "var(--color-primary-default, #9645FF)" }}
          >
            {formatPayrollCurrency(row.totalAmount, currency)}
          </span>
        ),
      }),
    ];

    if (showStcNumber) {
      baseColumns.push(
        textColumn({
          field: "stcNumber",
          headerName: labels.stcNo,
          autoFlex: false,
          flex: 0,
          width: 190,
          minWidth: 190,
          maxWidth: 190,
          renderCell: ({ row }) => (
            <span className="MuiDataGrid-cellContent">{row.stcNumber}</span>
          ),
        }),
      );
    }

    return baseColumns;
  }, [labels, currency, showStcNumber, lang]);

  return (
    <div className="event-payroll-generate__bulk-table-shell">
      <UITable
        className="event-payroll-generate__bulk-table"
        rows={pagedRows}
        columns={columns}
        contentSized
        rowHeight={72}
        headerHeight={56}
        striped={false}
        hideFooter
        disableSelectionOnClick
        sx={{ "& .MuiDataGrid-cell": { borderBottom: "none" } }}
      />
      <UIPaginationBar
        className="event-payroll-generate__bulk-pagination"
        currentPage={page}
        pages={pages}
        pageSize={pageSize}
        rangeTemplate={rangeTemplate}
        rangeFrom={rangeFrom}
        rangeTo={rangeTo}
        rangeTotal={total}
        rowsPerPageLabel={rowsPerPageLabel}
        onPageChange={setPage}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  );
};

EventPayrollBulkTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  labels: PropTypes.shape({
    id: PropTypes.string.isRequired,
    staff: PropTypes.string.isRequired,
    days: PropTypes.string.isRequired,
    shiftPay: PropTypes.string.isRequired,
    autoDeduction: PropTypes.string.isRequired,
    manualDeduction: PropTypes.string.isRequired,
    rewards: PropTypes.string.isRequired,
    trainingDays: PropTypes.string.isRequired,
    trainingPay: PropTypes.string.isRequired,
    totalAmount: PropTypes.string.isRequired,
    stcNo: PropTypes.string,
    roleCount: PropTypes.shape({
      one: PropTypes.string.isRequired,
      other: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  currency: PropTypes.string,
  showStcNumber: PropTypes.bool,
  rangeTemplate: PropTypes.string,
  rowsPerPageLabel: PropTypes.string,
};

EventPayrollBulkTable.defaultProps = {
  currency: "",
  showStcNumber: false,
  rangeTemplate: "",
  rowsPerPageLabel: "",
};

export default EventPayrollBulkTable;
